import type { MspTagOption } from '@/types/mspCustomer';
import type { ProvisioningToken } from '@/hooks/useProvisioningTokens';

/**
 * Decisions the customer detail page makes, kept out of the component.
 *
 * Everything here is pure, so the rules — what "suspended" resolves to for a
 * given organization, which tokens belong to a customer — are tested directly
 * rather than inferred from rendered markup. Same reasoning as `mspDashboardModel.ts`.
 */

/** Lifecycle values whose meaning the product relies on, when an org still has them. */
export const SUSPENDED_LIFECYCLE = 'suspended';
export const ACTIVE_LIFECYCLE = 'active';

/**
 * The lifecycle value this organization uses for a given meaning, if it has one.
 *
 * Looked up rather than hard-coded because the catalog is editable: an MSP can
 * rename a state or drop one it doesn't use, and sending a value the catalog no
 * longer offers is a 400. When it is missing, the action that depended on it is
 * unavailable — which is the honest answer.
 */
export function lifecycleOption(vocabulary: readonly MspTagOption[], value: string): MspTagOption | undefined {
  return vocabulary.find((option) => option.value === value);
}

/** The organization's default lifecycle, for returning a suspended customer to service. */
export function defaultLifecycle(vocabulary: readonly MspTagOption[]): MspTagOption | undefined {
  return vocabulary.find((option) => option.isDefault) ?? lifecycleOption(vocabulary, ACTIVE_LIFECYCLE);
}

/**
 * This customer's tokens that could still be used.
 *
 * `pending` only: a claimed or completed token has already done its job, and
 * offering to cancel it would suggest an effect it no longer has.
 */
export function pendingTokensFor(
  divisionId: string,
  tokens: readonly ProvisioningToken[] | undefined,
): ProvisioningToken[] {
  return (tokens ?? []).filter((t) => t.divisionId === divisionId && t.status === 'pending');
}

/** Contexts the caller could switch into for this customer. */
export function switchTargetsFor<T extends { id: string; name: string; divisionId?: string | null }>(
  divisionId: string,
  contexts: readonly T[] | undefined,
): T[] {
  return (contexts ?? []).filter((c) => c.divisionId === divisionId);
}
